import React, { useState, useEffect } from 'react';
import { Check, X, Sparkles, Loader2, Calendar, AlertCircle, Clock, Globe } from 'lucide-react';
import { Button } from '../common/Button';
import subscriptionService, { SubscriptionPlan, UserSubscription, CouponValidationResult } from '../../services/subscriptionService';
import { useToast } from '../common/Toast';
import PaymentModal from './PaymentModal';
import { GST_RATE } from '../../constants/gst';
import { locationService } from '../../services/locationService';
import { planTrialService } from '../../services/planTrialService';
import TrialRequestModal from './TrialRequestModal';
import { CollegeAvatars } from '../common/CollegeAvatars';
import { PricingTypeToggle, PricingType } from './PricingTypeToggle';

interface SubscriptionPlanProps {
  activePlan: 'free' | 'monthly' | 'yearly' | 'quarterly';
  userSubscription?: UserSubscription | null;
  onSubscriptionUpdate?: () => void;
  couponDetails?: CouponValidationResult | null;
  onPlanSelected?: (planId: string) => void;
  shouldShowPricing?: boolean;
  userLocation?: { country: string; countryCode: string } | null;
}

export const SubscriptionPlans: React.FC<SubscriptionPlanProps> = ({ 
  activePlan,
  userSubscription = null,
  onSubscriptionUpdate,
  couponDetails = null,
  onPlanSelected,
  shouldShowPricing = true,
  userLocation = null
}) => {
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const { showToast } = useToast();
  const [pricingType, setPricingType] = useState<PricingType>('per-interview');

  // Trial modal state
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [trialPlanName, setTrialPlanName] = useState<string>('');

  // Helper to determine if a plan is active
  const isPlanActive = (planType: string) => activePlan === planType;
  
  // Helper to check if a plan is a downgrade from current plan
  const isDowngrade = (planType: string) => {
    // If user has yearly, monthly would be a downgrade
    if ((activePlan === 'yearly' || activePlan === 'quarterly') && planType === 'monthly') {
      return true;
    }

    if (activePlan === 'yearly' && planType === 'quarterly') {
      return true;
    }

    return false;
  };

  // Helper to check if a plan is an upgrade from current plan
  const isUpgrade = (planType: string) => {
    // If user has free, any paid plan is an upgrade
    if (activePlan === 'free' && (planType === 'monthly' || planType === 'yearly' || planType === 'quarterly')) {
      return true;
    }
    // If user has monthly, yearly is an upgrade
    if (activePlan === 'monthly' && (planType === 'yearly' || planType === 'quarterly')) {
      return true;
    }

    if (activePlan === 'quarterly' && planType === 'yearly') {
      return true;
    }

    return false;
  };
  
  // Helper to check if user can purchase/upgrade to this plan
  const canPurchasePlan = (planType: string) => {
    // Active plans cannot be purchased again
    if (isPlanActive(planType)) {
      return false;
    }
    
    // Prevent downgrades (yearly to monthly)
    if (isDowngrade(planType)) {
      return false;
    }
    
    return true;
  };
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Get subscription plans that are not add-ons
        console.log('SubscriptionPlans: Fetching subscription plans...');
        const fetchedPlans = await subscriptionService.getSubscriptionPlansByType(false);
        console.log('SubscriptionPlans: Fetched subscription plans:', fetchedPlans);
        
        if (fetchedPlans.length === 0) {
          console.warn('SubscriptionPlans: No subscription plans returned from service');
        }
        
        setPlans(fetchedPlans);
        
        // If there are plans and onPlanSelected is provided, select the first plan by default
        if (fetchedPlans.length > 0 && onPlanSelected) {
          onPlanSelected(fetchedPlans[0]._id);
        }
        
        setError(null);
      } catch (err) {
        console.error('SubscriptionPlans: Error fetching subscription plans:', err);
        setError('Failed to load subscription plans. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [activePlan, onPlanSelected]);
  
  // Format date for display
  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric'
    });
  };
  
  // Handle plan trial request for non-Indian users
  const handleTrialRequest = async (plan: SubscriptionPlan) => {
    try {
      setIsProcessing(true);
      const response = await planTrialService.requestSubscriptionPlanTrial(
        plan._id,
        plan.name,
        plan.planType
      );

      if (response.success) {
        showToast(response.message, 'success');
      } else {
        showToast(response.message, 'error');
      }
    } catch (error) {
      console.error('Error requesting plan trial:', error);
      showToast('Failed to submit trial request. Please try again.', 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle subscription purchase/upgrade
  const handleSubscribe = async (plan: SubscriptionPlan) => {
    if (!canPurchasePlan(plan.planType)) {
      return;
    }
    
    // Set the selected plan
    setSelectedPlan(plan);
    
    // Notify parent about plan selection for coupon validation
    if (onPlanSelected) {
      onPlanSelected(plan._id);
    }
    
    // Check if this is a free plan
    if (plan.price === 0) {
      try {
        setIsProcessing(true);
        // Free plan - directly subscribe without payment
        const result = await subscriptionService.subscribeToPlan(plan);
        
        if (result) {
          showToast(`You have successfully subscribed to the ${plan.name} plan.`, 'success');
          window.location.reload();
        } else {
          throw new Error('Failed to subscribe to plan');
        }
      } catch (error) {
        console.error('Error subscribing to free plan:', error);
        showToast('Error subscribing to plan. Please try again.', 'error');
      } finally {
        setIsProcessing(false);
      }
      return;
    }
    
    // For paid plans, show payment modal
    setShowPaymentModal(true);
  };
  
  // Handle successful payment completion
  const handlePaymentSuccess = async () => {
    console.log('SubscriptionPlans: Payment successful');
    setShowPaymentModal(false);
    
    // Set 2 seconds delay for the subscription update
    await new Promise(resolve => setTimeout(resolve, 2000));
    console.log('SubscriptionPlans: Subscription update delayed');
    // Force refresh subscription data
    await subscriptionService.getUserSubscription(true);
    console.log('SubscriptionPlans: Subscription data refreshed');
    // Notify parent component about subscription update
    if (onSubscriptionUpdate) {
      onSubscriptionUpdate();
    }
    console.log('SubscriptionPlans: Subscription update completed');
    // Reload the page to refresh UI with updated subscription state
    window.location.reload();
  };
  
  // Handle payment error
  const handlePaymentError = (message: string) => {
    console.log('SubscriptionPlans: Payment error:', message);
    showToast(message || 'Payment processing failed', 'error');
    setIsProcessing(false);
    // close the payment modal
    setShowPaymentModal(false);
  };

  // Get the adjusted plan price if coupon is applied
  const getAdjustedPlanPrice = (plan: SubscriptionPlan) => {
    if (couponDetails && couponDetails.isValid) {
      // Check if coupon applies to a specific plan
      if (couponDetails.applicablePlans && couponDetails.applicablePlans.length > 0) {
        // Only apply discount if this plan is in the applicable plans list
        if (!couponDetails.applicablePlans.includes(plan._id)) {
          return plan.currentPrice; // No discount for non-applicable plans
        }
      }
      
      // First check if we have pre-calculated discounts for this plan from parent component
      if (couponDetails.planDiscounts && Array.isArray(couponDetails.planDiscounts)) {
        // Find the discount info for this specific plan
        const planDiscount = couponDetails.planDiscounts.find(
          (discount: any) => discount.planId === plan._id
        );
        
        if (planDiscount && typeof planDiscount.discountedPrice === 'number') {
          console.log(`Using pre-calculated discount for plan ${plan.name} (${plan._id}): ${planDiscount.discountedPrice}`);
          return planDiscount.discountedPrice;
        }
      }
      
      // If we have a single pre-calculated plan (legacy support)
      if (couponDetails.planId === plan._id && typeof couponDetails.discountedPrice === 'number' && couponDetails.discountedPrice > 0) {
        console.log(`Using legacy pre-calculated discount for plan ${plan.name} (${plan._id}): ${couponDetails.discountedPrice}`);
        return couponDetails.discountedPrice;
      }
      
      // If coupon is applicable to this plan but no specific calculation has been done yet,
      // calculate the discount on-the-fly based on coupon parameters
      if (isCouponApplicableForPlan(plan) && couponDetails.couponType !== 'free_minutes') {
        console.log(`Calculating discount on-the-fly for plan ${plan.name} (${plan._id})`);
        
        // Calculate discount based on coupon type
        if (couponDetails.couponType === 'percentage' && typeof couponDetails.value === 'number') {
          const discount = (plan.currentPrice * couponDetails.value) / 100;
          // Check if there's a maximum discount cap
          if (couponDetails.maxDiscountAmount && discount > couponDetails.maxDiscountAmount) {
            const cappedPrice = plan.currentPrice - couponDetails.maxDiscountAmount;
            console.log(`Capped discount: ${discount} > ${couponDetails.maxDiscountAmount}, price: ${cappedPrice}`);
            return cappedPrice;
          }
          const discountedPrice = plan.currentPrice - discount;
          console.log(`Percentage discount (${couponDetails.value}%): ${discount}, price: ${discountedPrice}`);
          return discountedPrice;
        } else if (couponDetails.couponType === 'fixed_amount' && typeof couponDetails.value === 'number') {
          const discountedPrice = Math.max(0, plan.currentPrice - couponDetails.value);
          console.log(`Fixed amount discount: ${couponDetails.value}, price: ${discountedPrice}`);
          return discountedPrice;
        }
      }
    }
    return plan.currentPrice;
  };

  // Check if coupon is applicable for this plan
  const isCouponApplicableForPlan = (plan: SubscriptionPlan): boolean => {
    if (!couponDetails || !couponDetails.isValid) {
      return false;
    }
    
    // Check for plan restrictions
    if (couponDetails.applicablePlans && couponDetails.applicablePlans.length > 0) {
      return couponDetails.applicablePlans.includes(plan._id);
    }
    
    // If no plan restrictions, coupon applies to all plans
    return true;
  };

  // Check if plan meets minimum purchase amount for coupon
  const meetsCouponMinimumPurchase = (plan: SubscriptionPlan): boolean => {
    if (!couponDetails || !couponDetails.isValid || !couponDetails.minPurchaseAmount) {
      return true; // No minimum requirement
    }
    
    return plan.currentPrice >= couponDetails.minPurchaseAmount;
  };

  // Get free minutes from coupon
  const getFreeMinutesFromCoupon = (plan: SubscriptionPlan) => {
    if (couponDetails && couponDetails.isValid && isCouponApplicableForPlan(plan)) {
      return couponDetails.freeMinutes || 0;
    }
    return 0;
  };

  // If loading, show a loading state
  if (loading) {
    return (
      <div className="text-base flex flex-col items-center justify-center py-12">
        <Loader2 className="w-12 h-12 text-primary-light dark:text-primary-dark animate-spin mb-4" />
        <p className="text-text-secondary-light dark:text-text-secondary-dark">
          Loading subscription plans...
        </p>
      </div>
    );
  }

  // If there was an error, show an error message
  if (error) {
    return (
      <div className="p-6 rounded-xl bg-red-500/10 text-center">
        <p className="text-red-500 dark:text-red-400">{error}</p>
        <Button 
          variant="secondary"
          className="mt-4"
          onClick={() => window.location.reload()}
        >
          Retry
        </Button>
      </div>
    );
  }

  // If no plans are available, show a message
  if (plans.length === 0) {
    return (
      <div className="p-6 rounded-xl bg-surface-light dark:bg-surface-dark text-center">
        <p className="text-text-secondary-light dark:text-text-secondary-dark">No subscription plans are currently available.</p>
      </div>
    );
  }

  // Default Plan Selection View
  return (
    <div>
      <h2 className="text-xl font-heading font-bold text-text-primary-light dark:text-text-primary-dark mb-6">
        Choose Your Plan
      </h2>
      
      {/* Pricing Type Toggle */}
      <PricingTypeToggle 
        activeType={pricingType}
        onTypeChange={setPricingType}
      />
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans
          .filter(plan => {
            // Filter by isPurchasable
            if (plan.isPurchasable === false) return false;
            
            // Filter by pricing type
            if (pricingType === 'per-interview') {
              return plan.planType === 'per_interview';
            } else {
              // For subscription, show monthly, yearly, quarterly (not per-interview or addon)
              return plan.planType !== 'per_interview' && !plan.isAddon;
            }
          })
          .map((plan) => {
          const isActive = isPlanActive(plan.planType);
          const expiryDate = isActive && userSubscription ? formatDate(userSubscription.endDate) : '';
          const canPurchase = canPurchasePlan(plan.planType);
          const isDowngradeAttempt = isDowngrade(plan.planType);
          const isUpgradeOption = isUpgrade(plan.planType);
          
          // When a plan is clicked, notify parent for coupon validation
          const handlePlanClick = () => {
            if (onPlanSelected) {
              onPlanSelected(plan._id);
            }
          };
          
          // Check if this plan has a coupon applied and is valid for it
          const hasCouponApplied = couponDetails?.isValid && isCouponApplicableForPlan(plan);
          
          // Check if plan meets minimum purchase requirement for coupon
          const meetsMinimumPurchase = meetsCouponMinimumPurchase(plan);
          
          // Apply discounted price if coupon is valid for this plan
          const displayPrice = getAdjustedPlanPrice(plan);
          const hasDiscount = hasCouponApplied && displayPrice < plan.currentPrice;
          
          // Free minutes from coupon
          const freeMinutes = getFreeMinutesFromCoupon(plan);
          const totalMinutes = plan.interviewMinutes + freeMinutes;
          
          // Calculate discount percentage for UI
          const discountPercentage = hasDiscount ? 
            Math.round(((plan.currentPrice - displayPrice) / plan.currentPrice) * 100) : 0;

          return (
            <div key={plan._id} onClick={handlePlanClick}>
              <SubscriptionCard
                id={plan.planType}
                name={plan.name}
                price={displayPrice}
                originalPrice={hasDiscount ? plan.currentPrice : (plan.discountPercentage > 0 ? plan.price : undefined)}
                duration={plan.planType === 'per_interview' ? undefined : (plan.planType === 'monthly' ? 'month' : plan.planType === 'yearly' ? 'year' : '3 months')}
                features={formatFeatures(plan.features, totalMinutes, freeMinutes, plan.isUnlimited)}
                highlight={plan.planType === 'quarterly' || (plan.planType === 'per_interview' && plan.numberOfInterviews === 5)}
                minutes={totalMinutes}
                isUnlimited={plan.isUnlimited}
                interviews={calculateInterviews(totalMinutes, plan.isUnlimited)}
                isActive={isActive}
                expiryDate={expiryDate}
                hasSubscription={userSubscription !== null && userSubscription.endDate !== undefined}
                isDowngrade={isDowngradeAttempt}
                isUpgrade={isUpgradeOption}
                isProcessing={isProcessing}
                hasCouponApplied={hasCouponApplied}
                hasDiscount={hasDiscount || plan.discountPercentage > 0}
                discountPercentage={discountPercentage || plan.discountPercentage}
                couponValidButNotApplicable={couponDetails?.isValid && !isCouponApplicableForPlan(plan)}
                couponMinPurchaseNotMet={!meetsMinimumPurchase && hasCouponApplied}
                freeMinutes={freeMinutes}
                gstRate={GST_RATE}
                shouldShowPricing={shouldShowPricing}
                userLocation={userLocation}
                onSubscribe={() => {
                  setSelectedPlan(plan);
                  handleSubscribe(plan);
                }}
                onTrialRequest={() => handleTrialRequest(plan)}
                planObj={plan}
              />
            </div>
          );
        })}
      </div>
      {activePlan === 'free' && shouldShowPricing ? (
        <div className="flex items-center text-amber-500 space-x-2 mb-4 mt-16">
          <AlertCircle size={16} />
          <span className="text-sm font-medium">Requires a paid plan subscription to buy add-ons</span>
        </div>
      ) : (
        <div className="flex items-center text-amber-500 space-x-2 mb-4 mt-16">
        </div>
      )}
      {!shouldShowPricing && (
        <div className="flex items-center text-amber-500 space-x-2 mb-4 mt-12">
          <AlertCircle size={16} />
          <span className="text-sm font-medium">We don't support payments from <span className="text-text-primary-light dark:text-text-primary-dark">{userLocation?.country}</span> yet. But you can request a plan trial.</span>
        </div>
      )}
      {/* Payment Modal */}
      {selectedPlan && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          planId={selectedPlan._id}
          planName={selectedPlan.name}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
          isRecurring={selectedPlan.isRecurring}
          couponCode={couponDetails?.isValid && isCouponApplicableForPlan(selectedPlan) ? couponDetails.code : undefined}
          couponDiscount={couponDetails?.isValid && isCouponApplicableForPlan(selectedPlan) ? couponDetails.discountAmount : undefined}
          couponFreeMinutes={couponDetails?.isValid && isCouponApplicableForPlan(selectedPlan) ? couponDetails.freeMinutes : undefined}
        />
      )}

      {/* Trial Request Modal */}
      <TrialRequestModal
        visible={showTrialModal}
        onClose={() => setShowTrialModal(false)}
        planName={trialPlanName}
        userLocation={userLocation}
      />
    </div>
  );
};

// Helper function to format features with interview minutes
const formatFeatures = (features: string[], minutes: number, freeMinutes: number = 0, isUnlimited: boolean): { text: string; available: boolean; highlight?: boolean }[] => {
  // Create a default first feature about interview minutes
  const minutesFeature = {
    text: isUnlimited ? 'Unlimited AI Interview Practice & Feedback' : `${minutes} minutes (${calculateInterviews(minutes, isUnlimited)} interviews) of AI Interview Practice & Feedback`,
    available: true,
    highlight: freeMinutes > 0
  };
  
  // Add free minutes mention if applicable
  if (freeMinutes > 0) {
    minutesFeature.text = isUnlimited ? 'Unlimited AI Interview Practice & Feedback' : `${minutes} minutes (includes ${freeMinutes} free minutes)`;
  }
  
  // Map the rest of the features
  const restOfFeatures = features.map(feature => ({
    text: feature,
    available: true
  }));
  
  return [minutesFeature, ...restOfFeatures];
};

// Helper function to calculate approximate number of interviews based on minutes
const calculateInterviews = (minutes: number, isUnlimited: boolean): string => {
  if (isUnlimited) {
    return '∞';
  }
  // Assuming an average interview is about 15-20 minutes
  const minInterviews = Math.floor(minutes / 18);
  const maxInterviews = Math.ceil(minutes / 15);
  
  return minInterviews === maxInterviews 
    ? `${minInterviews}` 
    : `${minInterviews}-${maxInterviews}`;
};

interface SubscriptionCardProps {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  duration?: string;
  features: { text: string; available: boolean; highlight?: boolean }[];
  highlight: boolean;
  minutes: number;
  interviews: string;
  isActive: boolean;
  expiryDate?: string;
  hasSubscription?: boolean;
  isDowngrade?: boolean;
  isUpgrade?: boolean;
  isUnlimited?: boolean;
  isProcessing?: boolean;
  hasCouponApplied?: boolean;
  hasDiscount?: boolean;
  discountPercentage?: number;
  couponValidButNotApplicable?: boolean;
  couponMinPurchaseNotMet?: boolean;
  freeMinutes?: number;
  gstRate: number;
  shouldShowPricing: boolean;
  userLocation?: { country: string; countryCode: string } | null;
  onSubscribe: () => void;
  onTrialRequest: () => void;
  planObj: SubscriptionPlan;
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({
  id,
  name,
  price,
  originalPrice,
  duration,
  features,
  highlight,
  minutes,
  interviews,
  isUnlimited,
  isActive,
  expiryDate,
  hasSubscription = false,
  isDowngrade = false,
  isUpgrade = false,
  isProcessing = false,
  hasCouponApplied = false,
  hasDiscount = false,
  discountPercentage = 0,
  couponValidButNotApplicable = false,
  couponMinPurchaseNotMet = false,
  onSubscribe,
  onTrialRequest,
  planObj,
  freeMinutes,
  gstRate,
  shouldShowPricing,
  userLocation
}) => {
  return (
    <div className={`
      group relative rounded-3xl p-px isolate overflow-hidden
      ${highlight ? 'bg-gradient-to-br from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark' : 'bg-gradient-to-br from-border-light via-primary-light/20 to-border-light dark:from-border-dark dark:via-primary-dark/20 dark:to-border-dark'}
      transition-all duration-300 hover:scale-[1.02]
    `}>
      {/* Animated gradient border */}
      <div className="absolute inset-0 bg-gradient-to-r from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark opacity-0 group-hover:opacity-20 transition-opacity duration-300 animate-gradient bg-[length:200%_auto]" />
      
      {/* Glow effect */}
      <div className={`
        absolute -inset-px rounded-3xl blur-xl
        ${highlight ? 'bg-gradient-to-br from-primary-light via-secondary-light to-primary-light dark:from-primary-dark dark:via-secondary-dark dark:to-primary-dark opacity-40' : 'opacity-0'}
        transition-opacity duration-300 group-hover:opacity-40
      `} />

      {/* Coupon badge */}
      {(hasCouponApplied || discountPercentage > 0) && (
        <div className="absolute -top-0 -right-0 z-10">
          <div className="bg-green-500 text-white px-4 py-1 rounded-bl-lg rounded-tr-xl text-sm font-semibold font-heading">
            {discountPercentage}% Off {hasCouponApplied ? 'Applied' : ''}
          </div>
        </div>
      )}

      {/* Coupon badge */}
      {highlight && (
            <div className="absolute -top-0 -left-0 z-10">
          <div className="bg-blue-600 text-white px-4 py-1 rounded-br-lg rounded-tl-xl text-sm font-semibold font-heading">
           Popular
          </div>
        </div>
      )}
      {/* Coupon badge */}
     {freeMinutes !== undefined && freeMinutes > 0 && (
        <div className="absolute -top-0 -right-0 z-10">
          <div className="bg-green-500 text-white px-4 py-1 rounded-bl-lg rounded-tr-xl text-sm font-semibold font-heading">
            Free additional {freeMinutes} minutes
          </div>
        </div>
      )}
      {/* Coupon not applicable badge, Commented out for now as it's not needed */}
      {/* {couponValidButNotApplicable && (
        <div className="absolute -top-0 -right-0 z-10">
          <div className="bg-amber-500 text-white px-3 py-1 rounded-bl-lg rounded-tr-xl text-xs font-medium shadow-md">
            Coupon not applicable
          </div>
        </div>
      )} */}


      {/* Glass container - Increased height with min-h-[600px] */}
      <div className="relative h-full min-h-[600px] flex flex-col rounded-[1.4rem] bg-gradient-to-br from-background-light/90 to-surface-light/90 dark:from-background-dark/90 dark:to-surface-dark/90 backdrop-blur-xl">
        {/* Content with consistent padding (same for all cards) */}
        <div className="px-8 pt-4 pb-4 flex-1 mt-4">
          {/* Header */}
          <div className="text-center mb-8 mt-4">
            <h3 className="text-2xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary-light via-text-primary-light to-primary-light dark:from-primary-dark dark:via-text-primary-dark dark:to-primary-dark">
              {name}
            </h3>
            <div className="flex items-baseline justify-center gap-2 mb-4">
              {shouldShowPricing ? (
                <>
                  {originalPrice && (
                    <span className="text-lg text-text-secondary-light dark:text-text-secondary-dark line-through opacity-75">
                      ₹{originalPrice.toFixed(0)}
                    </span>
                  )}
                  <span className="text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark">
                    {price === 0 ? '₹1' : `₹${price.toFixed(0)}`}
                  </span>
                  {price > 0 && duration && (
                    <span className={`text-text-secondary-light dark:text-text-secondary-dark ${duration.length > 6 ? 'text-base md:text-lg' : 'text-lg'}`}>
                      /{duration}
                    </span>
                  )}
                </>
              ) : (
                <div className="text-center">
                  <span className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary-light to-secondary-light dark:from-primary-dark dark:to-secondary-dark">
                    Request Plan Trial
                  </span>
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-2 max-w-md mx-auto">
                    We don't support payments from <span className="text-text-primary-light dark:text-text-primary-dark">{userLocation?.country}</span> yet. But you can request a trial plan.
                  </p>
                </div>
              )}
            </div>
            {/* GST Text Display - Only show for Indian users and if GST rate is greater than 0 */}
            {shouldShowPricing && price > 0 && gstRate > 0 && (
              <div className="text-center mb-4">
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark font-heading font-medium">
                  <span className="text-text-primary-light dark:text-text-primary-dark">+{gstRate}% GST</span> on the amount
                </p>
              </div>
            )}
            <div className="inline-flex items-center justify-center px-4 py-2 rounded-full bg-primary-light/10 dark:bg-primary-dark/10">
              <Clock size={18} className="mr-2 text-primary-light dark:text-primary-dark" />
              <span className="text-base font-medium text-primary-light dark:text-primary-dark">
                {isUnlimited ? (<><div className="flex flex-row items-center gap-1 h-[24px]">
                      <span className="text-2xl">∞ </span> <span className="">Minutes</span>
                      </div>
                      </>) : `${minutes} Minutes`}
              </span>
            </div>
          </div>

          {/* Features */}
          <ul className="space-y-4 mb-6">
            {features.filter(feature => feature.text.trim() !== '').map((feature, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className={`mt-0.5 p-1 rounded-full ${feature.available 
                  ? 'bg-gradient-to-br from-primary-light/20 to-secondary-light/20 dark:from-primary-dark/20 dark:to-secondary-dark/20' 
                  : 'bg-red-500/10'}`}
                >
                  {feature.available 
                    ? <Check className="w-3.5 h-3.5 text-primary-light dark:text-primary-dark" />
                    : <X className="w-3.5 h-3.5 text-red-500" />
                  }
                </span>
                <span className={`${feature.available 
                  ? 'text-base text-text-secondary-light dark:text-text-secondary-dark'
                  : 'text-base text-text-tertiary-light dark:text-text-tertiary-dark line-through opacity-75'}`}
                >
                  {feature.text}
                </span>
              </li>
            ))}
          </ul>

          {/* Job Readiness Stack Section */}
          <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-primary-light/5 to-secondary-light/5 dark:from-primary-dark/5 dark:to-secondary-dark/5 border border-border-light/50 dark:border-border-dark/50">
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mb-3 text-center">
              Includes Job Readiness Stack
            </p>
            <div className="flex flex-row flex-wrap items-center justify-center gap-6">
              {/* JobProt Logo and Text */}
              <a
                href="https://jobprot.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-xl font-bold transition-all duration-200 hover:scale-105 hover:opacity-80 cursor-pointer bg-gradient-to-r from-blue-900 to-blue-700 bg-clip-text text-transparent"
                style={{ 
                  fontFamily: 'Montserrat, sans-serif'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  // Track click
                  console.log('JobProt Link clicked');
                }}
              >
                <img 
                  src="/jobprot-logo.svg" 
                  alt="JobProt" 
                  className="w-6 h-6" 
                />
                JobProt
              </a>
              {/* Learnifie Logo and Text */}
              <a
                href="https://learnifie.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-xl font-bold transition-all duration-200 hover:scale-105 hover:opacity-80 cursor-pointer"
                style={{ 
                  fontFamily: 'Space Grotesk, sans-serif',
                  color: '#1E293B'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  // Track click
                  console.log('Learnifie Link clicked');
                }}
              >
                <img 
                  src="/learnifie-logo.svg" 
                  alt="Learnifie" 
                  className="w-6 h-6" 
                />
                Learnifie
              </a>
            </div>
          </div>
        </div>

        {/* Button and expiry date in a fixed position at the bottom with consistent padding */}
        <div className="px-6 pb-8 mt-auto">
          {/* CTA Button */}
          <Button 
            fullWidth={true}
            variant={isActive ? "secondary" : highlight ? "primary" : "secondary"}
            onClick={shouldShowPricing ? onSubscribe : onTrialRequest}
            disabled={isActive || (shouldShowPricing && isDowngrade) || isProcessing}
            className={`
              ${isActive ? 'cursor-not-allowed text-text-primary-light dark:text-text-primary-dark' : ''}
              ${isDowngrade ? 'opacity-50 cursor-not-allowed' : ''}
              text-lg py-3
            `}
            rightIcon={!isActive && !isDowngrade && !isProcessing && <Sparkles className="w-4 h-4" />}
            loading={isProcessing}
          >
            {isActive ? 'Current Plan' : 
             isDowngrade ? 'Cannot Downgrade' : 
            isProcessing ? 'Processing...' : 
            !shouldShowPricing ? 'Request Plan Trial' :
            isUpgrade && hasSubscription ? 'Upgrade Plan' : 'Choose Plan'}
          </Button>
          
          {/* Show expiry date below CTA button if this is the active plan */}
          {isActive && expiryDate && planObj.planType !== 'free' && (
            <div className="mt-4 flex items-center justify-center text-amber-500 text-sm">
              <Calendar size={14} className="mr-1" />
              <span>{planObj.isRecurring && planObj.isActive ? 'Renews on' : 'Expires on'} {expiryDate}</span>
            </div>
          )}
        </div>
      </div>
      
    </div>
  );
}; 